import { type Currency } from "./Product";

export type PendingOrder = {
  id: string;
  quantity: number;
};

export enum DiscountStatus {
  Active = "active",
  Suspended = "suspended",
  Deactivated = "deactivated",
  Archive = "archive",
}

export enum DiscountType {
  Percent = "percent",
  Number = "number",
}

export enum DiscountDurationScope {
  Once = "once",
  Forever = "forever",
}

export enum DiscountStripeSyncStatus {
  Synced = "synced",
  Pending = "pending",
  Failed = "failed",
}

export enum DiscountStripeSyncErrorCode {
  IncompatibleCouponType = "incompatible_coupon_type",
  IncompatibleCouponValue = "incompatible_coupon_value",
  IncompatibleCouponCurrency = "incompatible_coupon_currency",
  PromotionCodeUnusable = "promotion_code_unusable",
}

export enum StripePromotionCodeUnusableReason {
  Inactive = "inactive",
  Exhausted = "exhausted",
  Expired = "expired",
  CouponInvalid = "coupon_invalid",
}

export enum DiscountStripeReplaceReason {
  ImmutableFieldChange = "immutable_field_change",
}

export interface DiscountStripeSync {
  couponId: string;
  promotionCodeId: string;
  connectedAccountId: string;
  syncedAt: Date;
  syncStatus: DiscountStripeSyncStatus;
  syncError?: string | null;
  syncErrorCode?: DiscountStripeSyncErrorCode | null;
  syncErrorParams?: Record<string, string> | null;
}

export interface DiscountStripeSyncHistoryEntry extends DiscountStripeSync {
  replacedAt: Date;
  reason: DiscountStripeReplaceReason;
}

export interface DiscountDocument {
  id: string;
  code: string;
  type: DiscountType;
  value: number;
  currency: Currency | null;
  products: { id: string }[];
  prices: null | { id: string; productId: string }[];
  status: DiscountStatus;
  active: boolean;
  createdAt: Date;
  updatedAt: Date;
  ownerId: string;
  quantityLimit: null | number;
  limitRange: {
    from: Date;
    to: Date | null;
  } | null;
  used: number;
  pendingOrders?: PendingOrder[];
  durationScope?: DiscountDurationScope;
  stripe?: DiscountStripeSync | null;
  stripeHistory?: DiscountStripeSyncHistoryEntry[] | null;
}

export enum DiscountAction {
  Create = "create",
  Update = "update",
  Remove = "remove",
  Archive = "archive",
  ToggleVisibility = "toggle_visibility",
}

export interface BaseDiscountDTO {
  action: DiscountAction;
}

export interface CreateDiscountDTO {
  code: string;
  type: DiscountType;
  value: number;
  currency: Currency | null;
  products: { id: string }[];
  prices: null | { id: string; productId: string }[];
  status: DiscountStatus;
  active: boolean;
  quantityLimit: null | number;
  limitRange: {
    from: Date;
    to: Date | null;
  } | null;
  durationScope: DiscountDurationScope;
}

export interface UpdateDiscountDTO {
  action: DiscountAction.Update;
  id: string;
  type: DiscountType;
  value: number;
  currency: Currency | null;
  products: { id: string }[];
  prices: null | { id: string; productId: string }[];
  status: DiscountStatus;
  active: boolean;
  quantityLimit: null | number;
  limitRange: {
    from: Date;
    to: Date | null;
  } | null;
  durationScope: DiscountDurationScope;
}

export interface ArchiveDiscountDTO extends BaseDiscountDTO {
  action: DiscountAction.Archive;
  id: string;
}

export interface RemoveDiscountDTO extends BaseDiscountDTO {
  action: DiscountAction.Remove;
  id: string;
}

export interface ToggleDiscountVisibilityDTO extends BaseDiscountDTO {
  action: DiscountAction.ToggleVisibility;
  id: string;
  active: boolean;
}

export type DiscountDTO = UpdateDiscountDTO | ArchiveDiscountDTO | ToggleDiscountVisibilityDTO | RemoveDiscountDTO;

export interface DiscountCodeResponse {
  amountOff: number;
  percentOff: number;
  isFullyDiscounted: boolean;
}

/**
 * Per-redemption ledger row (`discount_redemption` table). One row per
 * successful payment that used an admin-managed discount; written by
 * `DiscountService.redeemFor*` from both Stripe and Tpay flows. The
 * `discount.used` counter coexists as the fast cache for limit validation.
 */
export interface DiscountRedemption {
  id: string;
  discountId: string;
  ownerId: string;
  code: string;
  orderId: string;
  productId: string | null;
  sessionId: string | null;
  customerId: string | null;
  clientEmail: string | null;
  paymentGateway: "STRIPE" | "TPAY";
  originalAmount: number | null;
  finalAmount: number | null;
  discountAmount: number | null;
  currency: string | null;
  redeemedAt: string;
}

/**
 * DB-row shape of the `discount` table (dates are ISO strings; nested structures
 * are stored as jsonb). The domain layer works with `DiscountDocument`.
 */
export interface Discount {
  id: string;
  ownerId: string;
  code: string;
  type: DiscountType;
  value: number;
  currency: Currency | null;
  products: { id: string }[];
  prices: null | { id: string; productId: string }[];
  status: DiscountStatus;
  active: boolean;
  quantityLimit: null | number;
  limitRange: { from: string | Date; to: string | Date | null } | null;
  used: number;
  pendingOrders: PendingOrder[] | null;
  durationScope: DiscountDurationScope | null;
  stripe: DiscountStripeSync | null;
  stripeHistory: DiscountStripeSyncHistoryEntry[] | null;
  createdAt: string;
  updatedAt: string;
}
